// src/pages/ProductsPage.js
import React, { useContext, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import CartDrawer from "../components/CartDrawer";
import AddedToBagToast from "../components/AddedToBagToast";
import { PRODUCT_CATEGORIES, PRODUCT_TAGS } from "../data/products";
import { getAllProducts } from "../services/products.api";
import { mapBackendProduct } from "../utils/mapProduct";
import { getPublicCategories } from "../services/categories.api";
import { useSeo } from "../hooks/useSeo";
import { apiRequest } from "../services/api";

function ProductsPage() {
  const { lang } = useContext(LanguageContext);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

const isIOS =
  typeof navigator !== "undefined" &&
  /iPad|iPhone|iPod/.test(navigator.userAgent);

const isAndroid =
  typeof navigator !== "undefined" &&
  /Android/i.test(navigator.userAgent);

const isSafari =
  typeof navigator !== "undefined" &&
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  const SPECIAL_SECTIONS = ["new-in", "bestsellers", "sale"];

  const urlCategory = searchParams.get("category");

const [initialSection, setInitialSection] = useState(
  urlCategory || location.state?.section || null
);


const PAGE_SIZE =
  isIOS || isSafari
    ? 12
    : isAndroid
    ? 16
    : 30;

const [currentPage, setCurrentPage] = useState(1);

   const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

const seoItems = useMemo(() => {
  return products.slice(0, 20);
}, [products]);


 useSeo(
  "category",
  initialSection && !SPECIAL_SECTIONS.includes(initialSection)
    ? initialSection
    : null,
  seoItems.length
    ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: seoItems.map((p, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `${window.location.origin}/product/${p.slug}`,
          name: lang === "EN" ? p.nameEN : p.nameAR,
        })),
      }
    : null
);



  React.useEffect(() => {
  if (location.state?.from === "home") {
    apiRequest("/analytics/track", {
      method: "POST",
      body: JSON.stringify({
        action_type: "enter_products",
        entity_type: "page",
      }),
    }).catch(() => {});
  }
}, []);


const searchFromUrl = searchParams.get("search");
const searchQuery = searchFromUrl || location.state?.search || null;

  const [allCategories, setAllCategories] = useState([]);

  const [allVariantProducts, setAllVariantProducts] = useState([]);

  // ✅ Sticky WhatsApp CTA
const [showWhatsappCTA, setShowWhatsappCTA] = useState(false);

// ✅ Scroll-to-top button visibility
const [showScrollTop, setShowScrollTop] = useState(false);



const whatsappMessage =
  lang === "EN"
    ? "Hi, I need help choosing products from your store."
    : "مرحباً، أحتاج مساعدة في اختيار المنتجات.";

const whatsappLink = `https://wa.me/96179338100?text=${encodeURIComponent(
  whatsappMessage
)}`;

React.useEffect(() => {
  setAllVariantProducts(products);
}, [products]);


    // FILTER STATE
const [categoryFilter, setCategoryFilter] = useState(() => {
  const section = location.state?.section;
  if (section && !SPECIAL_SECTIONS.includes(section) && !searchQuery) {
    return [section];
  }
  return [];
});



  React.useEffect(() => {
  let mounted = true;
  setLoading(true);

  const filters = {};

// ✅ SEARCH
if (searchQuery) {
  filters.search = searchQuery;
}


if (initialSection === "new-in") {
  filters.new = true;
}

if (initialSection === "bestsellers") {
  filters.bestseller = true;
}

if (initialSection === "sale") {
  filters.sale = true;
}

/* ✅ CATEGORY FILTER (disabled during search) */
if (
  !searchQuery &&                         // 👈 KEY LINE
  initialSection &&
  !SPECIAL_SECTIONS.includes(initialSection) &&
  categoryFilter.length <= 1
) {
  filters.category = initialSection;
}

  getAllProducts(filters)
    .then((data) => {
      if (mounted) {
        setProducts(data.map(mapBackendProduct));
      }
    })
    .catch(console.error)
    .finally(() => setLoading(false));

  return () => {
    mounted = false;
  };
}, [initialSection, searchQuery, categoryFilter]);


  React.useEffect(() => {
  if (location.state?.section) {
    setInitialSection(location.state.section);

    // Reset filters when changing category
    const section = location.state.section;

setCategoryFilter(
  section && !SPECIAL_SECTIONS.includes(section) ? [section] : []
);


    setSizeFilter([]);
    setColorFilter([]);
    setHeelFilter("all");
    
  }
}, [location.state?.section, location.state?.forceRefresh]);



  const [sizeFilter, setSizeFilter] = useState([]);
  const [colorFilter, setColorFilter] = useState([]);

  const [heelFilter, setHeelFilter] = useState("all");


  // SORT + UI
  const [sortOption, setSortOption] = useState("bestSelling");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [gridMode, setGridMode] = useState("two"); 

  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(5000);

  // PRICE RANGE
  const maxProductPrice = useMemo(() => {
  return products.reduce((max, p) => {
    const finalPrice = p.discount
      ? Math.round(p.price * (1 - p.discount / 100))
      : p.price;
    return finalPrice > max ? finalPrice : max;
  }, 0);
}, [products]);


  React.useEffect(() => {
  if (priceMax === null && maxProductPrice > 0) {
    setPriceMax(maxProductPrice);
  }
}, [maxProductPrice, priceMax]);


   // ✅ Delay WhatsApp CTA for better UX
React.useEffect(() => {
  const t = setTimeout(() => setShowWhatsappCTA(true), 1000);
  return () => clearTimeout(t);
}, []);

// ✅ Show scroll-to-top button after scrolling
React.useEffect(() => {
  const onScroll = () => {
    setShowScrollTop(window.scrollY > 300);
  };

  window.addEventListener("scroll", onScroll);
  return () => window.removeEventListener("scroll", onScroll);
}, []);

React.useEffect(() => {
  getPublicCategories()
    .then(setAllCategories)
    .catch(console.error);
}, []);

React.useEffect(() => {
  if (urlCategory) {
    setInitialSection(urlCategory);
    setCategoryFilter(
      !SPECIAL_SECTIONS.includes(urlCategory) ? [urlCategory] : []
    );
  }
}, [urlCategory]);


  const TEXT = {
    EN: {
      titleBySection: {
        "new-in": "New In",
        bestsellers: "Bestsellers",
        sale: "Season Sale",
        [PRODUCT_CATEGORIES.HEELS]: "Heels",
        [PRODUCT_CATEGORIES.SNEAKERS]: "Sneakers",
        [PRODUCT_CATEGORIES.BOOTS]: "Boots",
        [PRODUCT_CATEGORIES.FLATS_LOAFERS]: "Flats & Loafers",
        [PRODUCT_CATEGORIES.SANDALS]: "Sandals",
        [PRODUCT_CATEGORIES.ACCESSORIES]: "Bags & Accessories",
      },
      filters: "Filters",
      sortBy: "Sort by",
      bestSelling: "Best Selling",
      priceLowHigh: "Price: Low to High",
      priceHighLow: "Price: High to Low",
      productsWord: "products",
      category: "Category",
      size: "Size",
      color: "Color",
      price: "Price",
      heelHeight: "Heel height",
      all: "All",
      low: "Low",
      mid: "Mid",
      high: "High",
      appliedFilters: "Applied filters",
      clear: "Clear",
      clearAll: "Clear all",
      apply: "Apply",
      min: "Min",
      max: "Max",
      noProducts: "No products found in this selection.",
    },
    AR: {
      titleBySection: {
        "new-in": "وصل حديثاً",
        bestsellers: "الأكثر مبيعاً",
        sale: "تخفيضات الموسم",
        [PRODUCT_CATEGORIES.HEELS]: "أحذية كعب",
        [PRODUCT_CATEGORIES.SNEAKERS]: "أحذية رياضية",
        [PRODUCT_CATEGORIES.BOOTS]: "بوت",
        [PRODUCT_CATEGORIES.FLATS_LOAFERS]: "فلات ولوفر",
        [PRODUCT_CATEGORIES.SANDALS]: "صنادل",
        [PRODUCT_CATEGORIES.ACCESSORIES]: "حقائب وإكسسوارات",
      },
      filters: "تصفية",
      sortBy: "ترتيب حسب",
      bestSelling: "الأكثر مبيعاً",
      priceLowHigh: "السعر: من الأقل للأعلى",
      priceHighLow: "السعر: من الأعلى للأقل",
      productsWord: "منتج",
      category: "الفئة",
      size: "المقاس",
      color: "اللون",
      price: "السعر",
      heelHeight: "ارتفاع الكعب",
      all: "الكل",
      low: "منخفض",
      mid: "متوسط",
      high: "عالٍ",
      appliedFilters: "الفلاتر المفعّلة",
      clear: "مسح",
      clearAll: "مسح الكل",
      apply: "تطبيق",
      min: "الحد الأدنى",
      max: "الحد الأقصى",
      noProducts: "لا توجد منتجات ضمن هذه التصفية.",
    },
  }[lang];
  
 const pageTitle =
  TEXT.titleBySection[initialSection] ||
  allCategories.find(c => c.slug === initialSection)?.[
    lang === "EN" ? "name_en" : "name_ar"
  ] ||
  TEXT.titleBySection["new-in"];


const finalTitle = searchQuery
  ? `"${searchQuery}"`
  : pageTitle;


  // CATEGORY OPTIONS (for drawer)
 const CATEGORY_OPTIONS = allCategories.map((c) => ({
  key: c.slug,
  labelEN: c.name_en,
  labelAR: c.name_ar,
}));


  // SECTION (page) TAG FILTER
  /*const sectionTagFilterFn = (product) => {
    switch (initialSection) {
      case "new-in":
        return product.tags?.includes(PRODUCT_TAGS.NEW_IN);
      case "bestsellers":
        return product.tags?.includes(PRODUCT_TAGS.BESTSELLER);
      case "sale":
        return product.discount && product.discount > 0;
      default:
        return true;
    }
  };*/

  // AVAILABLE SIZES / COLORS
  const allSizes = useMemo(() => {
  const set = new Set();

  allVariantProducts.forEach((p) =>
    p.sizes?.forEach(({ size }) => {
      if (size === null || size === undefined) return;

      // block real NaN number
      if (typeof size === "number" && Number.isNaN(size)) return;

      // block empty strings
      if (typeof size === "string" && size.trim() === "") return;

      set.add(size);
    })
  );

  const arr = Array.from(set);

  // keep your smart sort
  return arr.sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isNaN(na) || Number.isNaN(nb))
      return String(a).localeCompare(String(b));
    return na - nb;
  });
}, [allVariantProducts]);



  const allColors = useMemo(() => {
  const c = [];
  allVariantProducts.forEach((p) =>
    p.colors?.forEach((col) => {
      if (!c.find((x) => x.name === col.name)) c.push(col);
    })
  );
  return c;
}, [allVariantProducts]);

const filterState = useMemo(
  () => ({
    initialSection,
    categoryFilter,
    sizeFilter,
    colorFilter,
    heelFilter,
    priceMin,
    priceMax,
    sortOption,
  }),
  [
    initialSection,
    categoryFilter,
    sizeFilter,
    colorFilter,
    heelFilter,
    priceMin,
    priceMax,
    sortOption,
  ]
);


  // MAIN FILTERING + SORTING
  const filteredAndSorted = useMemo(() => {
    let result = [...products];

 const {
    initialSection,
    categoryFilter,
    sizeFilter,
    colorFilter,
    heelFilter,
    priceMin,
    priceMax,
    sortOption,
  } = filterState;

    // Category filter (from drawer)
// Category filter (disabled during search unless user explicitly filters)
if (!searchQuery && categoryFilter.length > 0) {
  result = result.filter(
    (p) => p.categories?.some((c) => categoryFilter.includes(c))
  );
}

    // Size (single size)
    if (sizeFilter.length) {
  result = result.filter((p) =>
    p.sizes?.some(
      (s) => sizeFilter.includes(s.size) && s.inStock
    )
  );
}

    // Color
    if (colorFilter.length) {
  result = result.filter((p) =>
    p.colors?.some((c) => colorFilter.includes(c.name))
  );
}


    // Heel height (for heels or "new in" primarily)
    if (heelFilter !== "all") {
      result = result.filter((p) => {
        if (p.heelHeight == null) return false;
        if (heelFilter === "low") return p.heelHeight < 6;
        if (heelFilter === "mid") return p.heelHeight >= 6 && p.heelHeight <= 8;
        if (heelFilter === "high") return p.heelHeight > 8;
        return true;
      });
    }

    // Price range
    if (priceMax !== null) {
  result = result.filter((p) => {
    const finalPrice = p.discount
      ? Math.round(p.price * (1 - p.discount / 100))
      : p.price;
    return finalPrice >= priceMin && finalPrice <= priceMax;
  });
}

    // SORTING
    result = [...result];
    if (sortOption === "priceLowHigh") {
      result.sort((a, b) => {
        const pa = a.discount ? a.price * (1 - a.discount / 100) : a.price;
        const pb = b.discount ? b.price * (1 - b.discount / 100) : b.price;
        return pa - pb;
      });
    } else if (sortOption === "priceHighLow") {
      result.sort((a, b) => {
        const pa = a.discount ? a.price * (1 - a.discount / 100) : a.price;
        const pb = b.discount ? b.price * (1 - b.discount / 100) : b.price;
        return pb - pa;
      });
    } else {
      // "bestSelling" – prefer new items but keep base order
      result.sort((a, b) => (b.newIndex || 0) - (a.newIndex || 0));
    }

    return result;
}, [products, filterState]);

const totalPages = Math.max(
  1,
  Math.ceil(filteredAndSorted.length / PAGE_SIZE)
);

const paginatedProducts = useMemo(() => {
  const start = (currentPage - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  return filteredAndSorted.slice(start, end);
}, [filteredAndSorted, currentPage]);

React.useEffect(() => {
  setCurrentPage(1);
}, [
  initialSection,
  categoryFilter,
  sizeFilter,
  colorFilter,
  heelFilter,
  priceMin,
  priceMax,
  sortOption,
  searchQuery,
]);


  const showHeelFilter =
    initialSection === PRODUCT_CATEGORIES.HEELS || initialSection === "new-in";

  // GRID CLASSES
  const gridClasses =
    gridMode === "two"
      ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
      : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";

  // HELPERS
  const toggleCategory = (key) => {

  setCategoryFilter((prev) =>
    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
  );
};


  const clearAllFilters = () => {
  setCategoryFilter([]);
  setSizeFilter([]);
  setColorFilter([]);
  setHeelFilter("all");
  setPriceMin(0);
  setPriceMax(maxProductPrice || 400);
};


  // Applied filters (for chips)
  const appliedFilters = [];

  if (categoryFilter.length) {
    const labels = CATEGORY_OPTIONS.filter((c) =>
      categoryFilter.includes(c.key)
    )
      .map((c) => (lang === "EN" ? c.labelEN : c.labelAR))
      .join(", ");
    appliedFilters.push({
  id: "category",
  label: `${TEXT.category}: ${labels}`,
  onClear: () => setCategoryFilter([]),
});


  }
  if (sizeFilter.length) {
  appliedFilters.push({
    id: "size",
    label: `${TEXT.size}: ${sizeFilter.join(", ")}`,
    onClear: () => setSizeFilter([]),
  });
}

if (colorFilter.length) {
  appliedFilters.push({
    id: "color",
    label: `${TEXT.color}: ${colorFilter.join(", ")}`,
    onClear: () => setColorFilter([]),
  });
}

  if (heelFilter !== "all") {
    const heelLabel =
      heelFilter === "low"
        ? TEXT.low
        : heelFilter === "mid"
        ? TEXT.mid
        : TEXT.high;
    appliedFilters.push({
      id: "heel",
      label: `${TEXT.heelHeight}: ${heelLabel}`,
      onClear: () => setHeelFilter("all"),
    });
  }
  if (priceMin > 0 || priceMax < maxProductPrice) {
    appliedFilters.push({
      id: "price",
      label: `${TEXT.price}: $${Number(priceMin) || 0} – $${Number(priceMax) || 0}`,
      onClear: () => {
        setPriceMin(0);
        setPriceMax(maxProductPrice || 400);
      },
    });
  }

  const productCount = Number(filteredAndSorted.length) || 0;

  
  if (loading) {
  return (
    <div className="w-full min-h-screen flex items-center justify-center">
      <p className="text-sm text-[#7A7674]">Loading products…</p>
    </div>
  );
}

  return (
    <div className="w-full bg-white min-h-screen flex flex-col overflow-x-hidden">
      {/* TOP STRIP + HEADER */}
      <TopBanner />
      <MainHeader />
      <CartDrawer />
      <AddedToBagToast />

      {/* PAGE CONTENT */}
      <div
        className="w-full min-h-screen bg-white pt-[95px] px-4 pb-24"
        dir={lang === "AR" ? "rtl" : "ltr"}
      >
      {/* BACK BUTTON */}
<button
  onClick={() => navigate(-1)}
  className="
    absolute top-[110px]
    text-3xl text-[#2E3135]
    opacity-60 hover:opacity-100
  "
  style={lang === "AR" ? { right: "16px" } : { left: "16px" }}
>
  ×
</button>

        {/* PAGE TITLE */}
        <h1 className="text-xl font-semibold text-center tracking-wide text-[#2E3135] mt-2">
          {String(finalTitle || "").toUpperCase()}
        </h1>
        <div className="h-[2px] w-12 bg-black mx-auto mt-2 mb-4"></div>

        {/* FILTERS + SORT BAR */}
        <div className="flex gap-2 mb-3">
          {/* FILTERS BUTTON */}
          <button
            onClick={() => setIsFilterOpen(true)}
            className="
              flex-1 flex items-center justify-center gap-2
              border border-[#E6D8D2] rounded-md
              py-2.5 text-xs font-semibold tracking-wide
              bg-white active:scale-[0.98]
            "
          >
            <span>{TEXT.filters.toUpperCase()}</span>
            {/* simple filter icon */}
            <span className="flex flex-col gap-[2px]">
              <span className="w-3 h-[1.5px] bg-black rounded-full" />
              <span className="w-4 h-[1.5px] bg-black rounded-full" />
              <span className="w-2 h-[1.5px] bg-black rounded-full" />
            </span>
          </button>

          {/* SORT BUTTON + DROPDOWN */}
          <div className="relative flex-1">
            <button
              onClick={() => setIsSortOpen((p) => !p)}
              className="
                w-full flex items-center justify-center gap-2
                border border-[#E6D8D2] rounded-md
                py-2.5 text-xs font-semibold tracking-wide
                bg-white active:scale-[0.98]
              "
            >
              <span>{TEXT.sortBy.toUpperCase()}</span>
              <span className="text-[10px]">
                {isSortOpen ? "▲" : "▼"}
              </span>
            </button>

            {isSortOpen && (
              <div
                className="
                  absolute right-0 mt-1 w-44
                  bg-white border border-[#E6D8D2]
                  rounded-md shadow-lg z-20 text-xs
                "
                style={lang === "AR" ? { left: 0, right: "auto" } : {}}
              >
                <button
                  onClick={() => {
                    setSortOption("bestSelling");
                    setIsSortOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 hover:bg-[#F7F3F1] ${
                    sortOption === "bestSelling" ? "font-semibold" : ""
                  }`}
                >
                  {TEXT.bestSelling}
                </button>
                <button
                  onClick={() => {
                    setSortOption("priceLowHigh");
                    setIsSortOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 hover:bg-[#F7F3F1] ${
                    sortOption === "priceLowHigh" ? "font-semibold" : ""
                  }`}
                >
                  {TEXT.priceLowHigh}
                </button>
                <button
                  onClick={() => {
                    setSortOption("priceHighLow");
                    setIsSortOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 hover:bg-[#F7F3F1] ${
                    sortOption === "priceHighLow" ? "font-semibold" : ""
                  }`}
                >
                  {TEXT.priceHighLow}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* COUNT + GRID TOGGLE */}
        <div className="flex items-center justify-between mb-4">
          <span className="text-[11px] text-[#7A7674]">
            {String(productCount)} {TEXT.productsWord}
          </span>

          <div className="flex items-center gap-1">
            {/* 2-per-row grid icon */}
            <button
              onClick={() => setGridMode("two")}
              className={`
                w-7 h-7 border rounded-md flex items-center justify-center
                ${gridMode === "two" ? "bg-black text-white" : "border-[#E6D8D2]"}
              `}
            >
              <div className="grid grid-cols-2 grid-rows-2 gap-[2px] w-3 h-3">
                <span className="bg-current" />
                <span className="bg-current" />
                <span className="bg-current" />
                <span className="bg-current" />
              </div>
            </button>

            {/* 1-per-row (bigger cards) */}
            <button
              onClick={() => setGridMode("one")}
              className={`
                w-7 h-7 border rounded-md flex items-center justify-center
                ${gridMode === "one" ? "bg-black text-white" : "border-[#E6D8D2]"}
              `}
            >
              <div className="flex flex-col gap-[2px] w-3">
                <span className="h-[3px] bg-current rounded-sm" />
                <span className="h-[3px] bg-current rounded-sm" />
                <span className="h-[3px] bg-current rounded-sm" />
              </div>
            </button>
          </div>
        </div>

        {/* NO PRODUCTS */}
        {filteredAndSorted.length === 0 && (
          <p className="mt-10 text-center text-sm text-[#7A7674]">
            {TEXT.noProducts}
          </p>
        )}

        {/* PRODUCTS GRID */}
        <div className={`grid ${gridClasses} gap-3 md:gap-4`}>
          {paginatedProducts.map((p) => (
  <ProductCard key={p.id} product={p} />
))}
        </div>
      </div>
   
{totalPages > 1 && (
  <div className="flex justify-center items-center gap-2 mt-4 mb-8 flex-wrap">
    {/* PREV */}
    <button
      disabled={currentPage === 1}
      onClick={() => {
        setCurrentPage((p) => Math.max(1, p - 1));
        window.scrollTo({ top: 0, behavior: "smooth" });
      }}
      className={`
        px-3 py-1 border rounded-md text-xs font-semibold
        ${
          currentPage === 1
            ? "opacity-40 cursor-not-allowed"
            : "hover:bg-black hover:text-white"
        }
      `}
    >
      {lang === "EN" ? "Prev" : "السابق"}
    </button>

    {/* PAGE NUMBERS */}
    {Array.from({ length: totalPages }).map((_, i) => {
      const page = i + 1;
      return (
        <button
          key={page}
          onClick={() => {
            setCurrentPage(page);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className={`
            w-8 h-8 rounded-md border text-xs font-semibold
            ${
              currentPage === page
                ? "bg-black text-white border-black"
                : "border-[#E6D8D2] hover:bg-black hover:text-white"
            }
          `}
        >
          {page}
        </button>
      );
    })}

    {/* NEXT */}
    <button
      disabled={currentPage === totalPages}
      onClick={() => {
        setCurrentPage((p) => Math.min(totalPages, p + 1));
        window.scrollTo({ top: 0, behavior: "smooth" });
      }}
      className={`
        px-3 py-1 border rounded-md text-xs font-semibold
        ${
          currentPage === totalPages
            ? "opacity-40 cursor-not-allowed"
            : "hover:bg-black hover:text-white"
        }
      `}
    >
      {lang === "EN" ? "Next" : "التالي"}
    </button>
  </div>
)}
    
       {/* EXPLORE ALL PRODUCTS BUTTON (PAGE BOTTOM) */}
<div className="w-full flex justify-center">
  <button
    onClick={() => navigate("/all-products")}
    className="
      w-full max-w-md
      py-3 mb-6
      bg-black text-white
      rounded-lg
      text-sm font-semibold tracking-wide
      shadow-[0_4px_12px_rgba(0,0,0,0.25)]
      active:scale-[0.98]
      transition-all
    "
  >
    {lang === "EN" ? "EXPLORE ALL PRODUCTS" : "تصفح كل المنتجات"}
  </button>
</div>

      {/* FILTER DRAWER (LEFT) */}
      <div
        className={`
          fixed inset-0 z-[9999]
          transition-opacity duration-300
          ${isFilterOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}
        `}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40"
          onClick={() => setIsFilterOpen(false)}
        ></div>

        {/* Panel */}
        <div
          className={`
            absolute top-0 bottom-0 left-0
            w-[90%] max-w-sm bg-white
            shadow-2xl
            transform transition-transform duration-300
            ${isFilterOpen ? "translate-x-0" : "-translate-x-full"}
          `}
          dir={lang === "AR" ? "rtl" : "ltr"}
        >
          {/* HEADER */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-[#E6D8D2]">
            <span className="text-sm font-semibold">{TEXT.filters}</span>
            <button
              onClick={() => setIsFilterOpen(false)}
              className="text-2xl opacity-60 hover:opacity-100"
            >
              ×
            </button>
          </div>

          {/* CONTENT */}
<div className="h-[calc(100%-110px)] overflow-y-auto px-4 pt-3 pb-24 text-sm">

  {/* Applied Filters */}
  {appliedFilters.length > 0 && (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-[#2E3135]">
          {TEXT.appliedFilters}
        </span>
        <button
          onClick={clearAllFilters}
          className="text-[11px] text-[#7A7674] underline"
        >
          {TEXT.clearAll}
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {appliedFilters.map((f) => (
          <button
  key={f.id}
  onClick={f.onClear}
  disabled={!f.onClear}
  className={`
    flex items-center gap-1
    px-2 py-[3px]
    rounded-full bg-[#F1E7E2]
    text-[11px] text-[#2E3135]
    ${!f.onClear ? "opacity-60 cursor-not-allowed" : ""}
  `}
>

            <span>{f.label}</span>
            <span className="text-xs">×</span>
          </button>
        ))}
      </div>
    </div>
  )}

  {/* CATEGORY DROPDOWN */}
  <DrawerSection title={TEXT.category}>
    <div className="space-y-2 mt-2">
      {CATEGORY_OPTIONS.map((c) => {
        const label = lang === "EN" ? c.labelEN : c.labelAR;
        const checked = categoryFilter.includes(c.key);
        return (
          <label
            key={c.key}
            className="flex items-center gap-2 text-sm"
          >
            <input
  type="checkbox"
  checked={checked}
  onChange={() => toggleCategory(c.key)}
  className={`
    w-4 h-4 border-[#C9B7AE] rounded-sm
  `}
/>

            <span>{label}</span>
          </label>
        );
      })}
    </div>
  </DrawerSection>

  {/* SIZE DROPDOWN */}
  <DrawerSection title={TEXT.size}>
    <div className="flex flex-wrap gap-2 mt-2">
      {allSizes.map((s) => (
        <label
          key={String(s)}
          className="flex items-center gap-2 text-sm min-w-[70px]"
        >
          <input
            type="checkbox"
           checked={sizeFilter.includes(s)}
onChange={() =>
  setSizeFilter((prev) =>
    prev.includes(s)
      ? prev.filter((x) => x !== s)
      : [...prev, s]
  )
}
            className="w-4 h-4 border-[#C9B7AE] rounded-sm"
          />
          <span>{String(s)}</span>
        </label>
      ))}
    </div>
  </DrawerSection>

  {/* COLOR DROPDOWN */}
  <DrawerSection title={TEXT.color}>
    <div className="flex flex-wrap gap-3 mt-2">
      {allColors.map((c) => (
        <button
          key={c.name}
         onClick={() =>
  setColorFilter((prev) =>
    prev.includes(c.name)
      ? prev.filter((x) => x !== c.name)
      : [...prev, c.name]
  )
}

          className="flex flex-col items-center gap-1 text-[10px]"
        >
          <span
            className={`
              w-7 h-7 rounded-full border
              ${
                colorFilter.includes(c.name)
  ? "border-black"
  : "border-[#D3C5BE]"
              }
            `}
            style={{ backgroundColor: c.code }}
            title={c.name}
          ></span>
          <span className="text-[#3F4247]">{c.name}</span>
        </button>
      ))}
    </div>
  </DrawerSection>

  {/* HEEL HEIGHT */}
  {showHeelFilter && (
    <DrawerSection title={TEXT.heelHeight}>
      <div className="flex flex-wrap gap-2 mt-2">
        {[
          { key: "all", label: TEXT.all },
          { key: "low", label: TEXT.low },
          { key: "mid", label: TEXT.mid },
          { key: "high", label: TEXT.high },
        ].map((h) => (
          <button
            key={h.key}
            onClick={() => setHeelFilter(h.key)}
            className={`
              px-3 py-1 rounded-full border text-xs
              ${
                heelFilter === h.key
                  ? "bg-black text-white border-black"
                  : "border-[#E6D8D2] text-[#2E3135]"
              }
            `}
          >
            {h.label}
          </button>
        ))}
      </div>
    </DrawerSection>
  )}

  {/* PRICE SECTION */}
  <DrawerSection title={TEXT.price}>
    <div className="mt-3 space-y-3">
      {/* Inputs */}
      <div className="flex gap-3">
        <div className="flex-1">
          <p className="text-[11px] text-[#7A7674] mb-1">{TEXT.min}</p>
          <div className="flex items-center border border-[#E6D8D2] rounded-md px-2 py-1 text-xs">
            <span className="mr-1">$</span>
            <input
              type="number"
              value={priceMin}
              min={0}
              max={priceMax}
              onChange={(e) => {
                const val = Number(e.target.value) || 0;
                setPriceMin(Math.min(val, priceMax));
              }}
              className="w-full bg-transparent outline-none"
            />
          </div>
        </div>

        <div className="flex-1">
          <p className="text-[11px] text-[#7A7674] mb-1">{TEXT.max}</p>
          <div className="flex items-center border border-[#E6D8D2] rounded-md px-2 py-1 text-xs">
            <span className="mr-1">$</span>
            <input
              type="number"
              value={priceMax}
              min={priceMin}
              max={maxProductPrice}
              onChange={(e) => {
                const val = Number(e.target.value) || 0;
                setPriceMax(Math.max(val, priceMin));
              }}
              className="w-full bg-transparent outline-none"
            />
          </div>
        </div>
      </div>

      {/* Two Sliders */}
      <div className="mt-1 space-y-1">
        <input
          type="range"
          min={0}
          max={maxProductPrice}
          value={priceMin}
          onChange={(e) => {
            const val = Number(e.target.value);
            setPriceMin(Math.min(val, priceMax));
          }}
          className="w-full accent-black"
        />
        <input
          type="range"
          min={0}
          max={maxProductPrice}
          value={priceMax}
          onChange={(e) => {
            const val = Number(e.target.value);
            setPriceMax(Math.max(val, priceMin));
          }}
          className="w-full accent-black"
        />
      </div>
    </div>
  </DrawerSection>

  {/* ⭐ NEW BUTTON — EXPLORE ALL ⭐ */}
  <div className="w-full flex justify-center my-6">
    <button
      onClick={() => {
        setIsFilterOpen(false);
        navigate("/all-products");
      }}
      className="
        w-[90%] py-3 
        bg-black text-white 
        rounded-lg 
        text-sm font-semibold tracking-wide
        shadow-[0_4px_12px_rgba(0,0,0,0.25)]
        active:scale-[0.98] 
        transition-all
      "
    >
      {lang === "EN" ? "EXPLORE ALL PRODUCTS" : "تصفخ كل المنتجات"}
    </button>
  </div>
</div>


          {/* STICKY BOTTOM BUTTONS */}
          <div className="absolute bottom-0 left-0 right-0 border-t border-[#E6D8D2] bg-white px-4 py-3 flex gap-3">
            <button
              onClick={clearAllFilters}
              className="
                flex-1 py-2 border border-black rounded-md
                text-xs font-semibold tracking-wide
                bg-white
              "
            >
              {TEXT.clear.toUpperCase()}
            </button>
            <button
              onClick={() => setIsFilterOpen(false)}
              className="
                flex-1 py-2 rounded-md
                text-xs font-semibold tracking-wide
                bg-black text-white
              "
            >
              {TEXT.apply.toUpperCase()}
            </button>
          </div>
        </div>
      </div>
      {/* ✅ STICKY WHATSAPP CTA */}
{showWhatsappCTA && (
  <div
    className="fixed z-[9999] bottom-20 right-5 flex flex-col items-center gap-2"
    dir="ltr"
  >
    {/* Close button ABOVE */}
    <button
      onClick={() => setShowWhatsappCTA(false)}
      className="w-7 h-7 rounded-full bg-white border border-[#E6D8D2] flex items-center justify-center text-black text-sm shadow-md opacity-90 hover:opacity-100"
      aria-label="Close WhatsApp"
      title="Close"
    >
      ×
    </button>

    {/* WhatsApp Button */}
    <a
      href={whatsappLink}
      target="_blank"
      rel="noreferrer"
      className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg active:scale-95 transition"
      style={{ backgroundColor: "#25D366" }}
      aria-label="WhatsApp"
      title="WhatsApp"
    >
      <img
        src="/icons/whatsapp.png"
        alt="WhatsApp"
        className="w-7 h-7 object-contain"
      />
    </a>
  </div>
)}

    {/* ⬆️ SCROLL TO TOP BUTTON */}
{showScrollTop && (
  <button
    onClick={() =>
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
    className="
      fixed
      z-[9999]
      top-24
      left-1/2
      -translate-x-1/2
      w-12 h-12
      rounded-full
      bg-black
      text-white
      text-xl
      flex items-center justify-center
      shadow-lg
      opacity-90
      hover:opacity-100
      active:scale-95
      transition
    "
    aria-label="Scroll to top"
    title="Scroll to top"
  >
    ↑
  </button>
)}
<Footer className="mt-auto" />
    </div>
  );
}

/**
 * Small helper component for collapsible sections inside the filter drawer.
 */
function DrawerSection({ title, children }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="border-b border-[#F1E7E2] py-3">
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between text-sm font-semibold"
      >
        <span>{title}</span>
        <span className="text-xs">{open ? "▲" : "▼"}</span>
      </button>
      {open && <div className="mt-1">{children}</div>}
    </div>
  );
}

export default ProductsPage;
